import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from 'react-router';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HelpCircle, Search, MessageCircle, Phone, Mail, Clock, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useUserCountryConfig } from "@/lib/countryConfig";
import { isPlaceholderSupportNumber, supportContactLinks } from "@/lib/supportContactLinks";
import { useContactSettings } from '@/hooks/useContactSettings';
import { cn } from "@/lib/utils";
import { ChatSupport } from "@/components/support/ChatSupport";
import { OPEN_HELP_EVENT, OPEN_SUPPORT_CHAT_EVENT } from "@/lib/supportChat";
import { searchHelp } from "@/lib/helpSearch";
import { isHelperMode } from "@/components/BottomNav";

// The built-in answers, used until the help library has articles for the
// reader. Each is tagged with its audience like a library article, so the
// fallback follows the same audience rule (Codex on #1254).
const quickHelp: Array<{ audience: 'client' | 'helper' | 'all'; question: string; answer: string }> = [
  { audience: "client", question: "How do I hire a helper?", answer: "Browse service categories, view profiles, and contact helpers directly through our platform." },
  { audience: "all", question: "How are helpers verified?", answer: "Every helper provides a national ID and a Certificate of Good Conduct, passes automated identity checks and is approved by our team before they can take bookings." },
  { audience: "client", question: "What payment methods are accepted?", answer: "You pay in the app by mobile money or card, after the helper accepts your booking. Cash is not a payment method in the app." },
  { audience: "client", question: "Can I cancel a booking?", answer: "Yes, you can cancel at any time. A small cancellation fee is deducted from your refund to cover payment-processing costs; if your helper cancels, you are refunded in full." },
  { audience: "client", question: "How do I become a helper?", answer: "Register as a helper, complete your profile, and pass our verification process." },
  { audience: "client", question: "How do I check my booking status?", answer: "Go to My Bookings in your dashboard to see all upcoming and past bookings." },
  { audience: "client", question: "What if my helper doesn't show up?", answer: "Contact our support team immediately. We will help find a replacement or issue a refund." },
  { audience: "all", question: "How do I update my profile?", answer: "Go to your Profile tab in the dashboard to update your information, photo and preferences." },
];

// Hide the widget on pure admin routes and while a booking is actively in
// progress: the multi-step wizard's own sticky Back/Continue footer already
// occupies the same bottom-right zone the FAB floats over, and on a short
// mobile Safari viewport (URL bar + toolbar eating real estate) the FAB can
// end up sitting directly on top of a real control — e.g. the Booking
// Urgency toggle's "Urgent" option — blocking the tap, not just looking
// cluttered. "/booking/" (trailing slash) covers both /booking/:helperId
// (the actual wizard route — confirmed live, this is where the FAB was
// still overlapping "Cleaning"/"Urgent") and /booking/payment-complete,
// without matching /bookings (the booking list page, which should keep the
// FAB — it's not a focused multi-step form).
// /my-helpers excluded separately: its card list has a "..." menu trigger in
// the same bottom-right corner the FAB occupies, so cards scrolling into that
// corner visually collide with it (reported directly, not a #43x issue number).
const EXCLUDED_PATH_PREFIXES = [
  "/admin",
  "/uat/admin",
  "/booking-flow",
  "/booking/",
  "/messages",
  "/my-helpers",
];

// Helper-only screens outside BottomNav's helper routes (App.tsx wraps both
// in HelperRoute): the same exact paths the app shell treats as helper mode
// (src/mobile/shellTabs.ts). Exact, so the public /helper/:helperId booking
// profile, a client page, is not one.
const HELPER_ONLY_PATHS = new Set(['/helper/bookings', '/helper/my-services']);

/**
 * Whose help articles Quick Help shows here: none chosen (every audience)
 * for a visitor who is not signed in; helper articles in helper mode, as
 * BottomNav decides it; client articles anywhere else.
 */
function quickHelpAudience(pathname: string, signedIn: boolean): 'client' | 'helper' | null {
  if (!signedIn) return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  return isHelperMode(path) || HELPER_ONLY_PATHS.has(path) ? 'helper' : 'client';
}

/**
 * `floating={false}` keeps the panel (Help buttons elsewhere open it through
 * openHelp) but never shows the floating button: the Android app passes it
 * (owner, 12 Oct: "Two help buttons. Remove the floating one"), where Profile
 * has Help and the button only covered the page.
 */
export const HelpWidget = ({ floating = true }: { floating?: boolean } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The cookie sheet is 176px tall and sits at the bottom of the first-ever
  // session, right where this button lives. CookieConsent announces itself
  // when answered; until then, the same localStorage key it reads tells us
  // it is up.
  const [cookieVisible, setCookieVisible] = useState(() => {
    try {
      return !localStorage.getItem('cookie-consent');
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const onDismissed = () => setCookieVisible(false);
    window.addEventListener('cookieConsentDismissed', onDismissed);
    return () => window.removeEventListener('cookieConsentDismissed', onDismissed);
  }, []);
  // Out of the way while reading: the button floats over whatever card is
  // underneath (owner report: it sat on job cards and form fields), so it
  // slides off while the page scrolls down and comes back on any scroll up,
  // or at the top of the page.
  const [tucked, setTucked] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        if (y < 80) setTucked(false);
        else if (y > lastY + 6) setTucked(true);
        else if (y < lastY - 6) setTucked(false);
        lastY = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  // Signed in, "Send a message" goes into the chat (owner, 9 Oct): the
  // answer, and any staff reply, then show in one conversation.
  const [tab, setTab] = useState<string | null>(null);
  const [chatSeed, setChatSeed] = useState<string | null>(null);
  useEffect(() => {
    const onOpenChat = (e: Event) => {
      const message = (e as CustomEvent<{ message?: string }>).detail?.message;
      setIsOpen(true);
      setTab('chat');
      if (message?.trim()) setChatSeed(message.trim());
    };
    const onOpenHelp = (e: Event) => {
      // Admin routes render no widget: leave the event unanswered so the
      // caller falls back to the Support page.
      if (EXCLUDED_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix))) return;
      e.preventDefault();
      setIsOpen(true);
    };
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, onOpenChat);
    window.addEventListener(OPEN_HELP_EVENT, onOpenHelp);
    return () => {
      window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, onOpenChat);
      window.removeEventListener(OPEN_HELP_EVENT, onOpenHelp);
    };
  }, []);
  const [rated, setRated] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  // Links from "Kanfy Support replied" notifications (?help=chat) open the chat.
  useEffect(() => {
    if (user && new URLSearchParams(location.search).get('help') === 'chat') {
      setIsOpen(true);
      setTab('chat');
    }
  }, [user, location.search]);
  // The viewer's own market (account country or the one they switched to),
  // not the platform default, so the numbers match the rest of the site.
  const { config, marketSettled } = useUserCountryConfig();
  // Until the admin's settings arrive, contact is DEFAULT_CONTACT; an email
  // link then could open a draft to the wrong inbox, so it waits too.
  const { contact, isPending: contactPending } = useContactSettings();
  // Rules (own-market numbers only, international form, wa.me format) live
  // in lib/supportContactLinks; a number that fails them is a plain tile.
  const supportPhone = config.support?.phone ?? '';
  // No tile for a number the market has not set (the seed is a stand-in).
  const hasPhone = !isPlaceholderSupportNumber(supportPhone);
  const hasWhatsapp = !isPlaceholderSupportNumber(config.support?.whatsapp);
  // Unlinked until the market is settled: on a cold load config is briefly
  // the platform default, and a tap then would ring the wrong market.
  const { callHref, whatsappHref } = marketSettled ? supportContactLinks(config) : {};
  // Quick Help reads the help library (#1202), the same articles the AI
  // answers from; the built-in list is the fallback while it is empty.
  // Only the articles for whoever is reading: helper guidance (payouts) is
  // not shown to clients, nor client booking and payment steps to helpers.
  const audience = quickHelpAudience(location.pathname, !!user);
  const { data: libraryHelp } = useQuery({
    queryKey: ['help-articles-public', audience],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_help_articles', {
        p_limit: 50,
        ...(audience ? { p_audience: audience } : {}),
      });
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as { id: string; title: string; body: string }[];
    },
    staleTime: 10 * 60_000,
  });

  // Show on all pages except admin panels
  const isAdminRoute = EXCLUDED_PATH_PREFIXES.some(
    (prefix) => location.pathname.startsWith(prefix)
  );

  if (isAdminRoute) return null;

  // "Was this helpful?" feeds the help library's AI librarian (#1202).
  const rateArticle = async (articleId: string, helpful: boolean) => {
    setRated((r) => ({ ...r, [articleId]: helpful }));
    await supabase.rpc('rate_help_article', { p_article_id: articleId, p_helpful: helpful });
  };

  const helpItems = libraryHelp && libraryHelp.length > 0
    ? libraryHelp.map((a) => ({ id: a.id as string | undefined, question: a.title, answer: a.body }))
    : quickHelp
        .filter((q) => audience === null || q.audience === 'all' || q.audience === audience)
        .map(({ question, answer }) => ({ id: undefined as string | undefined, question, answer }));
  // By words, not the exact text typed: "Payments" finds "How do I pay"
  // (owner, 11 Oct; lib/helpSearch).
  const filteredHelp = searchHelp(helpItems, searchQuery);

  const handleSendMessage = () => {
    const text = supportMessage.trim();
    if (!text) return;
    if (!user) {
      toast({ title: "Please sign in", description: "Sign in to send a support message so you can see our reply.", variant: "destructive" });
      return;
    }
    setChatSeed(text);
    setSupportMessage("");
    setTab("chat");
  };


  return (
    // `bottom-20` was a hardcoded 80px that knew about none of the things it
    // has to clear: it sat on the booking flow's 91px action dock, cleared the
    // 74px tab bar by 6px, and disappeared entirely under the cookie sheet.
    // `.fab-bottom` is the repo's own answer to this (index.css) and was
    // written for this button — it just had no caller.
    <div
      className={cn(
        // No floating button on a phone where the site header is shown
        // (owner, 12 Oct: it covered prices, chips and fields). That header's
        // menu and the Profile pages open this panel instead (openHelp); the
        // sheet portals, so it still opens while its trigger is hidden.
        // `.help-fab` in index.css does the hiding.
        `help-fab fab-bottom${cookieVisible ? ' cookie-visible' : ''} fixed right-3 z-40 transition-[transform,opacity] duration-200 md:right-6`,
        tucked && !isOpen && "pointer-events-none translate-x-[calc(100%+1rem)] opacity-0",
        !floating && "hidden",
      )}
      data-tucked={tucked && !isOpen ? "true" : undefined}
    >
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            size="icon"
            className={cn(
              "h-11 w-11 rounded-full shadow-lg md:h-12 md:w-12",
              "bg-primary text-primary-foreground",
              "hover:bg-primary/90 hover:shadow-xl",
              "transition-all duration-200"
            )}
            aria-label="Open help"
            // Tucked is pointer-only: a keyboard user tabbing onto the button
            // brings it back into view rather than focusing something unseen.
            onFocus={() => setTucked(false)}
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="flex h-dvh w-full max-w-sm flex-col overflow-hidden p-0">
          <SheetHeader className="shrink-0 border-b border-border/60 px-4 pb-3 pt-5">
            <SheetTitle className="text-base font-semibold">Help &amp; Support</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">Find answers or contact our team</SheetDescription>
          </SheetHeader>
          {/* Signed-in customers open on a live chat: the AI assistant answers
              straight away, and "Talk to a person" hands over to staff (#1202). */}
          <Tabs value={tab ?? (user ? "chat" : "faq")} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList className={cn("mx-4 mb-1 mt-3 grid shrink-0", user ? "grid-cols-3" : "grid-cols-2")}>
              {user && <TabsTrigger value="chat" className="text-xs">Chat</TabsTrigger>}
              <TabsTrigger value="faq" className="text-xs">Quick Help</TabsTrigger>
              <TabsTrigger value="contact" className="text-xs">Contact Us</TabsTrigger>
            </TabsList>
            {user && (
              <TabsContent value="chat" className="mt-2 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden">
                {isOpen && (
                  <ChatSupport
                    initialMessage={chatSeed ?? undefined}
                    onInitialMessageSent={() => setChatSeed(null)}
                  />
                )}
              </TabsContent>
            )}
            <TabsContent value="faq" className="mt-2 min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  inputMode="search"
                  placeholder="Search help topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="space-y-2">
                {filteredHelp.length > 0 ? (
                  filteredHelp.map((item, idx) => (
                    <Card key={idx} className="border-border/60 bg-muted/40 shadow-sm">
                      <CardContent className="p-3">
                        <p className="text-xs font-medium text-foreground mb-1">{item.question}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.answer}</p>
                        {user && item.id && (
                          <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                            {rated[item.id] !== undefined ? (
                              <span>Thanks for the feedback</span>
                            ) : (
                              <>
                                <span>Helpful?</span>
                                <button type="button" className="underline" onClick={() => rateArticle(item.id as string, true)}>Yes</button>
                                <button type="button" className="underline" onClick={() => rateArticle(item.id as string, false)}>No</button>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="py-4 text-center" data-testid="quick-help-no-results">
                    <p className="text-sm text-muted-foreground">No help article matches &ldquo;{searchQuery}&rdquo; yet.</p>
                    {/* Nothing in the library: ask the assistant, which can
                        answer more than the articles cover (owner, 11 Oct). */}
                    {user ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => { setChatSeed(searchQuery.trim()); setTab('chat'); }}
                      >
                        Ask Kanfy AI
                      </Button>
                    ) : (
                      <Button type="button" size="sm" variant="outline" className="mt-3" onClick={() => setTab('contact')}>
                        Contact us
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="contact" className="mt-2 min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-6">
              <div className="grid grid-cols-2 gap-2">
                {/* The three contact tiles were plain boxes: nothing happened on
                    tap. They now dial, open the mail app and open WhatsApp. */}
                {[
                  hasPhone && { icon: Phone, label: "Call us", sub: supportPhone, href: callHref },
                  { icon: Mail, label: "Email us", sub: contact.support_email, href: !contactPending && contact.support_email ? `mailto:${contact.support_email}` : undefined },
                  hasWhatsapp && { icon: MessageCircle, label: "WhatsApp", sub: "Chat with us", href: whatsappHref, external: true },
                  { icon: Clock, label: "Hours", sub: hasPhone ? "Chat 24 hours · Calls 8am-6pm" : "Chat 24 hours" },
                ].filter((t) => !!t).map(({ icon: Icon, label, sub, href, external }) => {
                  const body = (
                    <>
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-xs font-medium">{label}</span>
                      <span className="text-[11px] text-muted-foreground text-center break-all">{sub}</span>
                    </>
                  );
                  const tile = "flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border/60 bg-muted/40";
                  return href ? (
                    <a
                      key={label}
                      href={href}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className={cn(tile, "transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring")}
                    >
                      {body}
                    </a>
                  ) : (
                    <div key={label} className={tile}>{body}</div>
                  );
                })}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Send a message</Label>
                <Textarea
                  placeholder="Describe your issue..."
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  className="text-sm min-h-[90px] resize-none"
                />
                <Button onClick={handleSendMessage} disabled={!supportMessage.trim()} className="w-full h-9 text-sm">
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Send Message
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </div>
  );
};
